'use client';

import { useState, type ElementType } from 'react';
import { Box, ButtonBase, Divider, ListSubheader, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { avatarTone, MAIL_FOLDERS } from '@/lib/inbox/display';
import type { InboxMailLabel } from '@/lib/inbox/gmailLabels';
import type { InboxMailFolder } from '@/lib/inbox/types';
import { STAR } from './conversationActions';
import { INK, MUTED, PICKER_ITEM_SX, PICKER_PAPER_SX, WASH } from './ConversationList';

const CONTROL_HEIGHT = 38;
const MENU_MAX_HEIGHT = 420;
// Gmail's palette has near-white colours that would vanish on the menu.
const RING = 'rgba(0,0,0,0.12)';

/**
 * How Gmail's folders look, here and in the heading that replaces the tabs while one is open. Sent takes the text colour:
 * this is read while ConversationList, which supplies INK, is still loading, so it must not use it.
 */
export const FOLDER_VIEWS: Record<InboxMailFolder, { Icon: ElementType; color: string }> = {
  starred: { Icon: StarRoundedIcon, color: STAR },
  sent: { Icon: SendOutlinedIcon, color: 'inherit' },
};

const FOLDER_ORDER: InboxMailFolder[] = ['starred', 'sent'];

type Props = {
  labels: InboxMailLabel[];
  value: string;
  folder?: InboxMailFolder;
  onChange: (label: string) => void;
  /** Undefined is the inbox. */
  onFolder: (folder?: InboxMailFolder) => void;
};

/**
 * Gmail's folders and labels, as in its sidebar: Inbox, Starred and Sent, then the mailbox's labels. Matches the mailbox
 * picker beside it until something is picked, then shows what is open.
 */
export default function LabelPicker({ labels, value, folder, onChange, onFolder }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const dark = useTheme().palette.mode === 'dark';
  const toneOf = (name: string) => labels.find((label) => label.name === name)?.color ?? avatarTone(name, dark);
  const tone = value && !folder ? toneOf(value) : null;
  const inbox = !folder && !value;
  const close = () => setAnchor(null);
  const chooseLabel = (label: string) => {
    close();
    if (label !== value || folder) onChange(label);
  };
  const chooseFolder = (next?: InboxMailFolder) => {
    close();
    if (next !== folder || (!next && value)) onFolder(next);
  };
  const open = folder ? MAIL_FOLDERS[folder].label : value;
  const ButtonIcon = folder ? FOLDER_VIEWS[folder].Icon : LabelOutlinedIcon;
  const check = <CheckRoundedIcon sx={{ fontSize: 18, color: INK }} />;
  const itemText = (text: string) => (
    <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem', fontWeight: 600, color: INK, lineHeight: 1.3 }}>
      {text}
    </Typography>
  );
  return (
    <>
      <ButtonBase
        aria-label={open ? `${open}. Change folder or label` : 'Folders and labels'}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        title={open || 'Folders and labels'}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          flexShrink: 0,
          height: CONTROL_HEIGHT,
          maxWidth: { xs: 100, sm: 140 },
          pl: 1.25,
          pr: 0.75,
          gap: 0.5,
          borderRadius: 2,
          fontSize: '0.8125rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          bgcolor: tone ? tone.bg : WASH,
          color: tone ? tone.fg : INK,
          '&:focus-visible': { outline: `2px solid ${INK}`, outlineOffset: 2 },
        }}
      >
        <ButtonIcon sx={{ fontSize: 17, flexShrink: 0, opacity: folder ? 1 : 0.8, color: folder ? FOLDER_VIEWS[folder].color : undefined }} />
        {open && (
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {open}
          </Box>
        )}
        <ExpandMoreRoundedIcon sx={{ fontSize: 16, flexShrink: 0, opacity: 0.6 }} />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { ...PICKER_PAPER_SX, maxHeight: MENU_MAX_HEIGHT } } }}
      >
        <MenuItem selected={inbox} onClick={() => chooseFolder(undefined)} sx={PICKER_ITEM_SX}>
          <InboxOutlinedIcon sx={{ fontSize: 18, color: INK }} />
          {itemText('Inbox')}
          {inbox && check}
        </MenuItem>
        {FOLDER_ORDER.map((item) => {
          const { Icon, color } = FOLDER_VIEWS[item];
          return (
            <MenuItem key={item} selected={folder === item} onClick={() => chooseFolder(item)} sx={PICKER_ITEM_SX}>
              <Icon sx={{ fontSize: 18, color }} />
              {itemText(MAIL_FOLDERS[item].label)}
              {folder === item && check}
            </MenuItem>
          );
        })}
        {labels.length > 0 && <Divider sx={{ my: 0.5 }} />}
        {labels.length > 0 && (
          <ListSubheader disableSticky sx={{ lineHeight: '28px', px: 1.5, fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: MUTED, bgcolor: 'transparent' }}>
            Labels
          </ListSubheader>
        )}
        {labels.map(({ name }) => (
          <MenuItem key={name} selected={!folder && name === value} onClick={() => chooseLabel(name)} sx={PICKER_ITEM_SX}>
            <Box aria-hidden sx={{ width: 10, height: 10, mx: '4px', borderRadius: '3px', flexShrink: 0, bgcolor: toneOf(name).bg, boxShadow: `inset 0 0 0 1px ${RING}` }} />
            {itemText(name)}
            {!folder && name === value && check}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
